import { VESSEL_SKIN_ASSET, VesselSkin } from './vessel-skin';

/** Ownership disc drawn under a hull (Flutter `VesselRing`): viewer-relative, composited into the sprite. */
export const VESSEL_RING_OWN = '#005DFF';
export const VESSEL_RING_OPACITY = 0.4;
/** Disc diameter ÷ hull length, measured off Flutter's badges. */
export const VESSEL_RING_DIAMETER_TO_HULL = 1.25;

/** Hull length ÷ the artwork's longest viewBox edge (measured by Flutter per skin). */
const HULL_LENGTH_RATIO: Record<VesselSkin, number> = {
  sportfish: 1.0,
  downeast: 0.969,
  center_console: 0.97,
};

/**
 * Hull length on screen, in CSS px — one size for every boat (own or not) so no hull reads bigger
 * than another. Larger on desktop where the phone size reads as tiny. The own-boat disc is
 * hull x VESSEL_RING_DIAMETER_TO_HULL (about 52 / 72px): the middle ground between the old sprite
 * (65 / 90) and the legacy boat_self.png (40 / 56). Keep in sync with `--vessel-marker-size` in
 * home-page.component.scss and the legacy `icon-size` in home-page.component.ts.
 */
export function vesselHullPx(desktop: boolean): number {
  return desktop ? 58 : 42;
}

export const DESKTOP_MIN_WIDTH_PX = 769;
export const isDesktopViewport = (): boolean => window.innerWidth >= DESKTOP_MIN_WIDTH_PX;

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`failed to load ${url}`));
    img.src = url;
  });
}

/**
 * Rasterizes a skin (and optionally its ownership disc) to a square canvas whose side is the
 * disc diameter, at `dpr` device pixels per CSS pixel. Hand it to `map.addImage(..., { pixelRatio: dpr })`
 * and `icon-size: 1` draws it at exactly `hullPx` CSS pixels long. The hull is bow-up, so
 * `icon-rotate` takes the heading as-is.
 */
export async function rasterizeSkinSprite(
  skin: VesselSkin,
  hullPx: number,
  dpr: number,
  ring: string | null,
): Promise<ImageData> {
  const img = await loadImage(VESSEL_SKIN_ASSET[skin]);
  const side = Math.ceil(hullPx * VESSEL_RING_DIAMETER_TO_HULL * dpr);
  const canvas = document.createElement('canvas');
  canvas.width = side;
  canvas.height = side;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2d context unavailable');

  if (ring) {
    ctx.globalAlpha = VESSEL_RING_OPACITY;
    ctx.fillStyle = ring;
    ctx.beginPath();
    ctx.arc(side / 2, side / 2, side / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  // BoxFit.contain inside the square whose side makes the HULL come out `hullPx` long.
  const box = (hullPx / HULL_LENGTH_RATIO[skin]) * dpr;
  const natural = Math.max(img.naturalWidth, img.naturalHeight) || 1;
  const scale = box / natural;
  const w = img.naturalWidth * scale;
  const h = img.naturalHeight * scale;
  ctx.drawImage(img, (side - w) / 2, (side - h) / 2, w, h);
  return ctx.getImageData(0, 0, side, side);
}
